import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";

/**
 * Calcula la luminosidad relativa de un color según WCAG
 * Retorna un valor entre 0 (negro) y 1 (blanco)
 */
function getLuminance(hex: string): number {
  // Convertir hex a RGB
  const rgb = hexToRgb(hex);
  if (!rgb) return 0.5; // Valor por defecto si no se puede parsear

  // Normalizar valores RGB a 0-1
  const [r, g, b] = rgb.map((val) => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });

  // Calcular luminosidad relativa según WCAG
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Convierte un color hex a RGB
 */
function hexToRgb(hex: string): [number, number, number] | null {
  // Remover el # si existe
  const cleanHex = hex.replace("#", "");

  // Si es formato corto (3 caracteres), expandirlo
  const fullHex =
    cleanHex.length === 3
      ? cleanHex
          .split("")
          .map((char) => char + char)
          .join("")
      : cleanHex;

  if (fullHex.length !== 6) return null;

  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);

  return [r, g, b];
}

/**
 * Calcula el contraste entre dos colores según WCAG
 * Retorna un valor entre 1 (sin contraste) y 21 (máximo contraste)
 */
function getContrastRatio(color1: string, color2: string): number {
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Determina si un color es claro u oscuro basándose en su luminosidad
 * Retorna true si el color es oscuro (debe usar texto blanco)
 * Usa un umbral más conservador para asegurar mejor contraste
 */
function isDarkColor(hex: string): boolean {
  const luminance = getLuminance(hex);
  // Usar un umbral más bajo (0.35) para ser más conservador y asegurar mejor contraste
  // También verificar el contraste directo con blanco y negro
  const contrastWithWhite = getContrastRatio(hex, "#ffffff");
  const contrastWithBlack = getContrastRatio(hex, "#000000");

  // Si el contraste con blanco es mejor que con negro, usar blanco
  // O si la luminosidad es menor a 0.35, definitivamente usar blanco
  // Priorizar el contraste: si el contraste con blanco es al menos 1.2 veces mejor que con negro, usar blanco
  return (
    luminance < 0.35 ||
    (contrastWithWhite > contrastWithBlack &&
      contrastWithWhite / contrastWithBlack > 1.1)
  );
}

export function MessageBubble({
  role,
  content,
  kind = "normal",
}: {
  role: "user" | "assistant";
  content: string;
  kind?: "normal" | "alert";
}) {
  const isUser = role === "user";
  const isAlert = kind === "alert" && !isUser;
  const [textColor, setTextColor] = useState<"white" | "black">("white");

  useEffect(() => {
    if (isUser) {
      const calculateTextColor = () => {
        const isDarkTheme = document.documentElement.classList.contains("dark");
        if (!isDarkTheme) {
          setTextColor("white");
          return;
        }

        // Usar un método más directo: crear un elemento temporal para obtener el color computado
        // Esto maneja automáticamente variables CSS anidadas y cualquier formato de color
        const tempEl = document.createElement("div");
        tempEl.style.backgroundColor = "var(--primary)";
        tempEl.style.position = "absolute";
        tempEl.style.visibility = "hidden";
        tempEl.style.width = "1px";
        tempEl.style.height = "1px";
        document.body.appendChild(tempEl);

        const computedColor = getComputedStyle(tempEl).backgroundColor;
        document.body.removeChild(tempEl);

        // Convertir rgb/rgba a hex
        let actualColor = "#1e293b"; // Color por defecto oscuro
        const rgbMatch = computedColor.match(/\d+/g);
        if (rgbMatch && rgbMatch.length >= 3) {
          const r = parseInt(rgbMatch[0]);
          const g = parseInt(rgbMatch[1]);
          const b = parseInt(rgbMatch[2]);
          actualColor = `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
        }

        // Determinar el color del texto basándose en la luminosidad y contraste
        const shouldUseWhiteText = isDarkColor(actualColor);
        setTextColor(shouldUseWhiteText ? "white" : "black");
      };

      // Ejecutar inmediatamente y también después de un pequeño delay para asegurar que el DOM esté listo
      calculateTextColor();
      const timeoutId = setTimeout(() => {
        calculateTextColor();
      }, 100);

      // Observar cambios en las variables CSS
      const observer = new MutationObserver(() => {
        calculateTextColor();
      });

      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["style", "class"],
      });

      // También observar cambios en el tema (dark/light)
      const themeObserver = new MutationObserver(() => {
        calculateTextColor();
      });

      themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"],
      });

      return () => {
        clearTimeout(timeoutId);
        observer.disconnect();
        themeObserver.disconnect();
      };
    }
  }, [isUser]);

  const processContent = (content: string) => {
    if (typeof content !== "string") return content;
    return content.replace(/<br>(.*?)<br>/g, "**$1**");
  };

  return (
    <div
      className={`mb-2 flex ${
        isUser ? "justify-end" : "justify-start"
      } min-w-0`}
    >
      <div
        className={[
          // Slightly larger horizontal padding for user messages so text doesn't touch the edge
          `min-w-0 max-w-[80%] rounded-lg ${
            isUser ? "px-4" : "px-3 py-2"
          } text-base`,
          isAlert ? "border border-red-200 bg-red-50 text-red-800" : "",
          "break-words [overflow-wrap:break-word] [word-break:normal]",
          isUser
            ? `bg-[var(--primary)]`
            : isAlert
              ? ""
              : "bg-[var(--background-muted)] text-[var(--foreground)]",
        ].join(" ")}
        style={
          isUser
            ? {
                backgroundColor: "var(--primary)",
                color: textColor === "white" ? "#ffffff" : "#000000",
              }
            : isAlert
              ? {
                  color: "#991b1b",
                }
            : undefined
        }
      >
        {/* 👇 Aplica las clases tipográficas en un wrapper, NO en ReactMarkdown */}
        <div
          className={[
            "prose prose-base max-w-full dark:prose-invert",
            "prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-pre:my-2",
            "prose-pre:overflow-x-auto prose-pre:max-w-full",
            "[word-break:normal] [overflow-wrap:break-word]",
            isAlert ? "prose-strong:text-red-900" : "",
            isUser
              ? textColor === "white"
                ? "prose-code:bg-white/10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded"
                : "prose-code:bg-black/10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded"
              : "prose-code:bg-black/10 dark:prose-code:bg-white/10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded",
            "prose-img:max-w-full prose-img:h-auto",
            "prose-table:block prose-table:w-full prose-table:overflow-x-auto",
          ].join(" ")}
          style={
            isUser
              ? {
                  color: textColor === "white" ? "#ffffff" : "#000000",
                }
              : undefined
          }
        >
          <ReactMarkdown
            children={processContent(content)}
            remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
            rehypePlugins={[rehypeRaw, rehypeSanitize]}
            components={{
              p: ({ children }) => (
                <p
                  className="markdown-paragraph [word-break:normal] [overflow-wrap:break-word]"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </p>
              ),
              strong: ({ children }) => (
                <strong
                  className="markdown-bold [word-break:normal] [overflow-wrap:break-word]"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </strong>
              ),
              em: ({ children }) => (
                <em
                  className="markdown-italic [word-break:normal] [overflow-wrap:break-word]"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </em>
              ),
              h1: ({ children }) => (
                <h1
                  className="markdown-h1"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2
                  className="markdown-h2"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3
                  className="markdown-h3"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </h3>
              ),
              h4: ({ children }) => (
                <h4
                  className="markdown-h3"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </h4>
              ),
              h5: ({ children }) => (
                <h5
                  className="markdown-h3"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </h5>
              ),
              h6: ({ children }) => (
                <h6
                  className="markdown-h3"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </h6>
              ),
              ul: ({ children }) => (
                <ul
                  className="markdown-ul [word-break:normal] [overflow-wrap:normal]"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol
                  className="markdown-ol [word-break:normal] [overflow-wrap:normal]"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </ol>
              ),
              li: ({ children }) => (
                <li
                  className="markdown-li [word-break:normal] [overflow-wrap:break-word]"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  <span className="markdown-li-content" style={{ minWidth: 0 }}>
                    {children}
                  </span>
                </li>
              ),
              blockquote: ({ children }) => (
                <blockquote
                  className="markdown-blockquote"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </blockquote>
              ),
              code: ({ children }: any) => {
                return (
                  <code
                    className="markdown-code"
                    style={
                      isUser
                        ? {
                            color:
                              textColor === "white" ? "#ffffff" : "#000000",
                          }
                        : undefined
                    }
                  >
                    {children}
                  </code>
                );
              },
              pre: ({ children }) => (
                <pre
                  className="markdown-pre"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </pre>
              ),
              a: ({ href, children }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="markdown-a"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </a>
              ),
              table: ({ children }) => (
                <div className="markdown-table">
                  <table
                    style={
                      isUser
                        ? {
                            color:
                              textColor === "white" ? "#ffffff" : "#000000",
                          }
                        : undefined
                    }
                  >
                    {children}
                  </table>
                </div>
              ),
              thead: ({ children }) => (
                <thead
                  className="markdown-thead"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </thead>
              ),
              tbody: ({ children }) => (
                <tbody
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </tbody>
              ),
              tr: ({ children }) => (
                <tr
                  className="markdown-tr"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </tr>
              ),
              th: ({ children }) => (
                <th
                  className="markdown-th"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td
                  className="markdown-td"
                  style={
                    isUser
                      ? { color: textColor === "white" ? "#ffffff" : "#000000" }
                      : undefined
                  }
                >
                  {children}
                </td>
              ),
              hr: () => (
                <hr
                  className="markdown-hr"
                  style={
                    isUser
                      ? {
                          borderColor:
                            textColor === "white" ? "#ffffff" : "#000000",
                        }
                      : undefined
                  }
                />
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
}
